import {
  getReturnSpeedConstraints,
  getWaypointSpeedConstraints,
  WAYPOINT_GIMBAL_PITCH,
  WAYPOINT_HEADING,
  WAYPOINT_HEIGHT,
  WAYPOINT_PAUSE_TIME,
} from "./constraints";

import type { FlightMode, VehicleConfiguration } from "./constraints";
import type { MissionDetails, ReturnBehaviorData, WaypointData } from "./validators";

/**
 * Mission Editor Default Values
 *
 * This file contains functions to generate default values for mission editing.
 * These defaults are shared between cloud web and mobile platforms.
 */

/**
 * Create default mission details
 */
export function createDefaultMissionDetails(): MissionDetails {
  return {
    missionName: "New Mission",
    siteId: undefined,
    takeoffPointId: undefined,
    vehicleConfiguration: undefined,
  };
}

/**
 * Create default waypoint
 *
 * @param index - Waypoint index (for display/identification)
 * @param latitude - Optional latitude (defaults to undefined)
 * @param longitude - Optional longitude (defaults to undefined)
 * @param flightMode - Flight mode for speed defaults
 * @param vehicleConfig - Vehicle configuration for speed limits
 * @param isIndoor - Whether the mission is indoor (affects default speed)
 */
export function createDefaultWaypointData(
  _index: number, // Unused but kept for API compatibility
  latitude?: number,
  longitude?: number,
  flightMode: FlightMode = "nominal",
  vehicleConfig?: VehicleConfiguration,
  isIndoor: boolean = false
): WaypointData {
  const speedConstraints = getWaypointSpeedConstraints(flightMode, vehicleConfig);
  const defaultSpeed = isIndoor ? speedConstraints.DEFAULT_INDOOR : speedConstraints.DEFAULT;

  return {
    height: WAYPOINT_HEIGHT.DEFAULT,
    speed: defaultSpeed,
    heading: WAYPOINT_HEADING.DEFAULT,
    gimbalPitch: WAYPOINT_GIMBAL_PITCH.DEFAULT,
    pauseTime: WAYPOINT_PAUSE_TIME.DEFAULT,
    latitude,
    longitude,
  };
}

/**
 * Create default return behavior
 *
 * @param flightMode - Flight mode for speed defaults
 * @param vehicleConfig - Vehicle configuration for speed limits
 */
export function createDefaultReturnBehavior(
  flightMode: FlightMode = "nominal",
  vehicleConfig?: VehicleConfiguration
): ReturnBehaviorData {
  const speedConstraints = getReturnSpeedConstraints(flightMode, vehicleConfig);

  return {
    returnSpeed: speedConstraints.DEFAULT,
  };
}

/**
 * Complete mission data structure
 */
export interface MissionData {
  details: MissionDetails;
  waypoints: WaypointData[];
  returnBehavior: ReturnBehaviorData;
}

/**
 * Create a complete default mission
 *
 * @param initialWaypointCount - Number of waypoints to create initially (default: 1)
 * @param flightMode - Flight mode for constraints
 * @param vehicleConfig - Vehicle configuration
 */
export function createDefaultMission(
  initialWaypointCount: number = 1,
  flightMode: FlightMode = "nominal",
  vehicleConfig?: VehicleConfiguration
): MissionData {
  const waypoints: WaypointData[] = [];

  for (let i = 0; i < initialWaypointCount; i++) {
    waypoints.push(createDefaultWaypointData(i, undefined, undefined, flightMode, vehicleConfig));
  }

  return {
    details: createDefaultMissionDetails(),
    waypoints,
    returnBehavior: createDefaultReturnBehavior(flightMode, vehicleConfig),
  };
}

/**
 * Clamp a value between min and max
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(value, max));
}

/**
 * Clamp waypoint values to valid ranges
 *
 * @remarks
 * Useful when importing waypoints from external sources or user input
 */
export function clampWaypoint(
  waypoint: WaypointData,
  flightMode: FlightMode = "nominal",
  vehicleConfig?: VehicleConfiguration
): WaypointData {
  const speedConstraints = getWaypointSpeedConstraints(flightMode, vehicleConfig);

  return {
    height: clamp(waypoint.height, WAYPOINT_HEIGHT.MIN, WAYPOINT_HEIGHT.MAX),
    speed: clamp(waypoint.speed, speedConstraints.MIN, speedConstraints.MAX),
    heading: clamp(waypoint.heading, WAYPOINT_HEADING.MIN, WAYPOINT_HEADING.MAX),
    gimbalPitch: clamp(waypoint.gimbalPitch, WAYPOINT_GIMBAL_PITCH.MIN, WAYPOINT_GIMBAL_PITCH.MAX),
    pauseTime: clamp(waypoint.pauseTime, WAYPOINT_PAUSE_TIME.MIN, WAYPOINT_PAUSE_TIME.MAX),
    latitude: waypoint.latitude !== undefined ? clamp(waypoint.latitude, -90, 90) : undefined,
    longitude: waypoint.longitude !== undefined ? clamp(waypoint.longitude, -180, 180) : undefined,
  };
}

/**
 * Clamp return behavior values to valid ranges
 */
export function clampReturnBehavior(
  returnBehavior: ReturnBehaviorData,
  flightMode: FlightMode = "nominal",
  vehicleConfig?: VehicleConfiguration
): ReturnBehaviorData {
  const speedConstraints = getReturnSpeedConstraints(flightMode, vehicleConfig);

  return {
    returnSpeed: clamp(returnBehavior.returnSpeed, speedConstraints.MIN, speedConstraints.MAX),
  };
}
